import React, { useEffect, useRef } from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Heading, HStack, Link, Text } from '@chakra-ui/react';
import FinancialStatements from '../../components/FinancialStatements';
import StockEarningsReport from './StockEarningsReport';
import {
  DEFAULT_FINANCIAL_STATEMENT,
  getFinancialStatementBySlug,
  isKnownFinancialStatementSlug,
} from './financialStatementRoutes';

export function LegacyEarningsRedirect() {
  const { ticker } = useParams();
  return <Navigate replace to={`/stock/${ticker.toLowerCase()}/financials/earnings`} />;
}

function StockFinancialStatements() {
  const { ticker, statementSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEarnings = statementSlug === 'earnings';
  const selectedStatement = getFinancialStatementBySlug(statementSlug);
  const basePath = `/stock/${ticker.toLowerCase()}/financials`;
  const annualPath = useRef({ ticker, path: `${basePath}/${DEFAULT_FINANCIAL_STATEMENT.slug}` });
  if (annualPath.current.ticker !== ticker) {
    annualPath.current = { ticker, path: `${basePath}/${DEFAULT_FINANCIAL_STATEMENT.slug}` };
  }
  if (isKnownFinancialStatementSlug(statementSlug)) {
    annualPath.current.path = location.pathname + location.search;
  }

  useEffect(() => {
    if (!isEarnings && !isKnownFinancialStatementSlug(statementSlug)) {
      navigate(`${basePath}/${DEFAULT_FINANCIAL_STATEMENT.slug}${location.search}`, { replace: true });
    }
  }, [basePath, isEarnings, location.search, navigate, statementSlug]);

  return (
    <Box p={4}>
      <Heading>Financial Statements</Heading>
      <Text fontSize="xl" mt={2}>{ticker.toUpperCase()}</Text>
      <HStack as="nav" aria-label="Financial statement views" gap={2} mt={4} mb={6} flexWrap="wrap">
        {[
          { label: 'Annual statements', to: annualPath.current.path, active: !isEarnings },
          { label: 'Most recent earnings', to: `${basePath}/earnings`, active: isEarnings },
        ].map(({ label, to, active }) => (
          <Link key={label} as={RouterLink} to={to} aria-current={active ? 'page' : undefined}
            px={4} py={2} borderWidth="1px" borderRadius="md" fontWeight={active ? 'semibold' : 'normal'}
            bg={active ? 'bg.muted' : 'transparent'}>{label}</Link>
        ))}
      </HStack>
      {isEarnings ? <StockEarningsReport embedded /> :
        <FinancialStatements ticker={ticker} selectedStatement={selectedStatement} />}
    </Box>
  );
}

export default StockFinancialStatements;
